import { mapGetters } from 'vuex';
import ContactlessSummary from './ContactlessSummary';
import PriceCard from './PriceCard';
import Button from '../../shared/Button';
import Accounting from '../../../mixins/accounting';
import ImageUrlFallback from '../../../mixins/imageUrlFallback';
import VehicleVisualSummary from './VehicleVisualSummary';
import VehicleCardLocalisations from '../../../lang/VehicleCard';
import BookingsLocalisations from '../../../lang/bookings';
import merge from 'lodash/merge';
import ProductDetails from '../ProductDetails';
import branding from '../../../mixins/branding';
require('../scss/vehicle-card.scss');
require('./scss/drive-go-vehicle-card.scss');

export default {
    name: 'drive-go-vehicle-card',

    components: {
        'gm-button': Button,
        PriceCard,
        VehicleVisualSummary,
        ContactlessSummary,
        ProductDetails,
    },

    mixins: [Accounting, ImageUrlFallback, branding],

    props: ['vehicle', 'buy', 'labels', 'locale', 'selectVehicle', 'showTermsAndConditions', 'handleLogin', 'experiment'],

    template: `
        <div class="vehicle drive-go-vehicle-card d-flex" v-if="currentVehicle">
            <vehicle-visual-summary :vehicle="vehicle" :labels="labels" :currentVehicle="currentVehicle" :currencyIsChanging="currencyIsChanging" :activeProduct="activeProduct" :experiment="experiment"></vehicle-visual-summary>
            <contactless-summary></contactless-summary>
            <div class="price-block-mobile">
                <div class="upgrade-product w-100" @click="toggleDetails">
                    <span>{{ $t('bookings.drive_and_go.premium_plus_benefits_also_included') }}</span>
                    <span class="upgrade-product-arrow" :class="{'active': showKeylessBenefits}"></span>
                </div>
                
                <transition name="fade">
                    <div class="mb-0 mt-4 w-100" v-if="shouldShowDetails">
                        <product-details
                            :show-terms-and-conditions="showTermsAndConditions"
                            :product="activeProduct"
                            :currency-is-changing="currencyIsChanging"
                            upsell-card-type="mobile-us-product-upsell"
                            :experiment="experiment"
                        ></product-details>
                    </div>
                </transition>

                <div class="book-default w-100">
                    <gm-button
                        @click="selectVehicleHandler(vehicle, activeProduct.id);"
                        v-if="vehicle.is_drive_and_go && activeProduct.is_available"
                        type="select"
                        :mobile-classes="true"
                        :disabled="disabledButtonStatus"
                    >
                        {{ $t('bookings.book_now') }}
                    </gm-button>
                    <gm-button
                        top-right-radius="0"
                        top-left-radius="0"
                        :disabled="true"
                        :mobile-classes="true"
                        v-if="!activeProduct.is_available"
                    >
                        {{ $t('VehicleCard.age_limit', { age: activeProduct.minimum_age }) }}
                    </gm-button>

                    <div class="align-items-end d-flex flex-column mb-0 p-0 price-card-total">
                        <span>
                            <strong class="original-amount" v-if="activeProduct.original_price && !currencyIsChanging">{{ originalPrice(activeProduct) }}</strong>
                            <strong class="amount" v-if="currencyIsChanging">&hellip;</strong>
                            <strong class="amount original-amount-mobile" v-else>{{ vehiclePrice }}</strong>
                            <strong class="text-lowercase gm-text-black">{{ labels.extraPerDay }}</strong>
                        </span>
                        <span>
                            <strong class="text-capitalize gm-text-black">{{ labels.total }}</strong>
                            <strong class="amount" v-if="currencyIsChanging">&hellip;</strong>
                            <strong class="gm-text-black" v-else>{{ price(activeProduct) }}</strong>
                        </span>
                    </div>
                </div>
            </div>
            <price-card
                :class="{ active: true }"
                :key="activeProduct.type"
                :product="activeProduct"
                :vehicle="currentVehicle"
                :labels="labels"
                :buy="buy"
                :show-terms-and-conditions="showTermsAndConditions"
                :currency-is-changing="currencyIsChanging"
                :select-vehicle="selectVehicleHandler"
                :selected-currency="selectedCurrency"
                :user="user"
                :handle-login="handleLogin"
                :experiment="experiment"
            ></price-card>
        </div>
    `,
    i18n: merge(VehicleCardLocalisations, BookingsLocalisations),
    data() {
        return {
            currencyIsChanging: false,
            disabledButtonStatus: false,
            imageErrorUrls: [],
            currentVehicle: null,
            product: null,
            showKeylessBenefits: false,
            products: [],
            shouldShowDetails: false,
            icons: {
                PMP: this.brandingImagesPath('/images/vehicle-listing/premium-plus-badge.svg'),
            },
            productLabels: {
                PMP: 'premium plus+',
            },
            vehiclePricePerDay: 0,
        };
    },

    mounted() {
        this.currentVehicle = this.vehicle;
        window.bus.$on('currency.changed', this.currencyChanging);
        window.bus.$on('vehicle.selected', this.disableButton);
        window.bus.$on('toggle-drige-go-details', this.toggleDetails);
    },

    beforeDestroy() {
        window.bus.$off('currency.changed', this.currencyChanging);
    },

    watch: {
        vehicle: function (vehicle) {
            this.currencyIsChanging = false;
        },
        quoteHireDuration: function (duration) {
            this.vehiclePricePerDay = this.originalPricePerDay(this.activeProduct, duration);
        },
    },

    computed: Object.assign(
        {
            vehiclePrice() {
                return this.quoteHireDuration > 1 ? this.originalPricePerDay(this.activeProduct, this.quoteHireDuration) : this.formatMoney(this.activeProduct.total_price, this.selectedCurrency.code);
            },
            activeProduct() {
                const activeProducts = this.vehicle.products.data.filter((product) => product.type === 'PMP');
                return activeProducts.length > 0 ? activeProducts[0] : {};
            },
            pluralizationEnabled() {
                return !['tr'].includes(this.locale);
            },
            userLoyalty() {
                if (!this.user) {
                    return null;
                }

                const data = this.user.loyalty_level.data;

                return {
                    name: data.name,
                    image: this.brandingImagesPath(`/images/loyalty-icons/medals/${data.slug}.svg`),
                };
            },
            loyaltyDiscountApplied() {
                return this.vehicle.loyalty_discount_applied && this.userLoyalty;
            },
            pickupLocationIsUK() {
                return this.pickupLocation.country && this.pickupLocation.country.data.id === 1;
            },
        },
        mapGetters({
            quotePaymentType: 'quotePaymentType',
            quoteHireDuration: 'quoteHireDuration',
            selectedCurrency: 'selectedCurrency',
            user: 'getQuoteUser',
            quote: 'getQuote',
            pickupLocation: 'getPickupLocation',
        })
    ),

    methods: {
        currencyChanging() {
            this.currencyIsChanging = true;
        },
        setActiveProduct(vehicle, product) {
            this.products = this.products.map((aProduct) => {
                aProduct.active = false;
                return aProduct;
            });
            product.active = true;
        },
        showBenefits() {
            this.showKeylessBenefits = !this.showKeylessBenefits;
        },
        disableButton() {
            this.disabledButtonStatus = true;
        },
        selectVehicleHandler(vehicle, activeProductId) {
            window.bus.$emit('vehicle.selected');
            window.bus.$emit('vehicle.selecting');
            this.selectVehicle(vehicle, activeProductId);
        },
        toggleDetails() {
            this.shouldShowDetails = !this.shouldShowDetails;
        },
    },
};
